import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { Button } from "reactstrap"
import { FilterData, IndexType } from "components/Extra"
import DeliveryMenTable from "pages/vendor/Delivery-men/DeliveryMenTable"
import { DeliveryMenGrid } from "pages/vendor/Delivery-men/DeliveryMenGrid"
import { AddDeliveryMan, BarcodeModal } from "pages/vendor/Delivery-men/Modals"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import barcodeImage from "./barcode.png"
import { getDeliveryMen, deleteDeliveryMan, updateDeliveryMan } from "../../../store/delivery-men/actions"

export interface DeliveryMan {
  id: number
  name: string
  mobile: string
  address: string | null
  overview: string | null
  isActive: boolean
}

interface DeliveryMenProps {
  isDashboardPage?: boolean
}

// Export empty array for backward compatibility (Orders module imports this)
// Real data comes from Redux
export const deliveryMenData: DeliveryMan[] = []

export const DeliveryMen: React.FC<DeliveryMenProps> = ({ isDashboardPage }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  // Get data from Redux
  const { deliveryMen, loading } = useSelector(
    (state: any) => state.DeliveryMen
  )

  const [activeTab, setActiveTab] = useState<IndexType>("list-ul")
  const [checkedDeliveryMen, setCheckedDeliveryMen] = useState<DeliveryMan[]>(
    []
  )
  const [filteredDeliveryMen, setFilteredDeliveryMen] = useState<DeliveryMan[]>(
    []
  )
  const [openAddDeliveryMan, setOpenAddDeliveryMan] = useState<boolean>(false)
  const [openBarcodeModal, setOpenBarcodeModal] = useState<boolean>(false)
  const [selectedDeliveryMan, setSelectedDeliveryMan] = useState<DeliveryMan | null>(null)

  // Get initial page from URL query params
  const getInitialPage = useCallback(() => {
    const params = new URLSearchParams(location.search)
    const pageParam = params.get('page')
    return pageParam ? Math.max(0, parseInt(pageParam) - 1) : 0
  }, [location.search])

  // Handle page change - update URL
  const handlePageChange = useCallback((pageIndex: number) => {
    const params = new URLSearchParams(location.search)
    params.set('page', (pageIndex + 1).toString())
    navigate(`${location.pathname}?${params.toString()}`, { replace: true })
  }, [location.search, location.pathname, navigate])

  // Load delivery men on mount
  useEffect(() => {
    dispatch(getDeliveryMen({ push: navigate }))
  }, [dispatch, navigate])

  // Update filtered delivery men when delivery men change
  useEffect(() => {
    setFilteredDeliveryMen(deliveryMen)
  }, [deliveryMen])

  const isAllChecked = useMemo(() => {
    return (
      deliveryMen.length > 0 && checkedDeliveryMen.length === deliveryMen.length
    )
  }, [deliveryMen, checkedDeliveryMen])

  const togglecheckedDeliveryManHandler = useCallback(
    (deliveryMan: DeliveryMan) => {
      setCheckedDeliveryMen(prev =>
        prev.includes(deliveryMan)
          ? prev.filter(d => d !== deliveryMan)
          : [...prev, deliveryMan]
      )
    },
    []
  )

  const toggleAllDeliveryMen = useCallback(() => {
    setCheckedDeliveryMen(isAllChecked ? [] : deliveryMen)
  }, [isAllChecked, deliveryMen])

  const toggleActiveDeliveryMan = useCallback(
    (deliveryMan: DeliveryMan) => {
      dispatch(updateDeliveryMan(
        deliveryMan.id,
        {
          name: deliveryMan.name,
          mobile: deliveryMan.mobile,
          address: deliveryMan.address || "",
          overview: deliveryMan.overview || "",
          isActive: !deliveryMan.isActive,
        },
        { push: navigate }
      ))
    },
    [dispatch, navigate]
  )

  const addNewDeliveryManHandler = useCallback(() => {
    setSelectedDeliveryMan(null)
    setOpenAddDeliveryMan(true)
  }, [])

  const openBarcodeModalHandler = useCallback(() => {
    setOpenBarcodeModal(true)
  }, [])

  const printDeliveredBarcodeHandler = useCallback((deliveryMan: DeliveryMan) => {
    setOpenBarcodeModal(true)
  }, [])

  const editDeliveryManHandler = useCallback((deliveryMan: DeliveryMan) => {
    setSelectedDeliveryMan(deliveryMan)
    setOpenAddDeliveryMan(true)
  }, [])

  const deleteDeliveryManHandler = useCallback((deliveryMan: DeliveryMan) => {
    dispatch(deleteDeliveryMan(deliveryMan.id, { push: navigate }))
  }, [dispatch, navigate, t])

  const viewDetailsHandler = useCallback((deliveryMan: DeliveryMan) => {
    navigate(`/delivery-men/${deliveryMan.id}`)
  }, [navigate])

  if (isDashboardPage) {
    return (
      <>
        <FilterData
          data={deliveryMen}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          bearerFilteredData={setFilteredDeliveryMen}
          showFilterBtnInsteadOfToggle
        />

        {/* Modals */}
        <AddDeliveryMan
          isOpen={openAddDeliveryMan}
          toggle={() => {
            setOpenAddDeliveryMan(false)
            setSelectedDeliveryMan(null)
          }}
          deliveryMan={selectedDeliveryMan}
        />
        <BarcodeModal
          isOpen={openBarcodeModal}
          toggle={() => setOpenBarcodeModal(false)}
        />
        <DeliveryMenTable
          deliveryMen={filteredDeliveryMen}
          isAllChecked={isAllChecked}
          checkedDeliveryMen={checkedDeliveryMen}
          toggleAllDeliveryMen={toggleAllDeliveryMen}
          togglecheckedDeliveryManHandler={togglecheckedDeliveryManHandler}
          toggleActiveDeliveryMan={toggleActiveDeliveryMan}
          editDeliveryManHandler={editDeliveryManHandler}
          deleteDeliveryManHandler={deleteDeliveryManHandler}
          viewDetailsHandler={viewDetailsHandler}
          printDeliveredBarcodeHandler={printDeliveredBarcodeHandler}
          initialPage={getInitialPage()}
          onPageChange={handlePageChange}
        />
      </>
    )
  }

  return (
    <div className="page-content">
      <div className="container-fluid d-flex justify-content-between align-items-center">
        <Breadcrumbs
          title={t("dashboard_dashboard")}
          breadcrumbItem={t("deliveryMen_deliveryMen")}
        />{" "}
        <div className="d-flex gap-2 align-items-center">
          <Button
            onClick={openBarcodeModalHandler}
            className="btn btn-light border d-flex align-items-center justify-content-center"
            style={{ minWidth: "auto", padding: "0.25rem 0.5rem" }}
          >
            <img
              src={barcodeImage}
              alt="Barcode"
              style={{ height: "32px", width: "auto" }}
            />
          </Button>
          <Button
            onClick={addNewDeliveryManHandler}
            className="bg-gradiant-blue text-capitalize btn btn-secondary border-0"
          >
            {t("deliveryMen_addNewDeliveryMan")}
          </Button>
        </div>
      </div>
      <FilterData
        data={deliveryMen}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        bearerFilteredData={setFilteredDeliveryMen}
      />

      {/* Modals */}
      <AddDeliveryMan
        isOpen={openAddDeliveryMan}
        toggle={() => {
          setOpenAddDeliveryMan(false)
          setSelectedDeliveryMan(null)
        }}
        deliveryMan={selectedDeliveryMan}
      />
      <BarcodeModal
        isOpen={openBarcodeModal}
        toggle={() => setOpenBarcodeModal(false)}
      />

      {activeTab == "list-ul" ? (
        <DeliveryMenTable
          deliveryMen={filteredDeliveryMen}
          isAllChecked={isAllChecked}
          checkedDeliveryMen={checkedDeliveryMen}
          toggleAllDeliveryMen={toggleAllDeliveryMen}
          togglecheckedDeliveryManHandler={togglecheckedDeliveryManHandler}
          toggleActiveDeliveryMan={toggleActiveDeliveryMan}
          editDeliveryManHandler={editDeliveryManHandler}
          deleteDeliveryManHandler={deleteDeliveryManHandler}
          viewDetailsHandler={viewDetailsHandler}
          printDeliveredBarcodeHandler={printDeliveredBarcodeHandler}
        />
      ) : (
        <DeliveryMenGrid
          deliveryMen={filteredDeliveryMen}
          editDeliveryManHandler={editDeliveryManHandler}
          deleteDeliveryManHandler={deleteDeliveryManHandler}
          viewDetailsHandler={viewDetailsHandler}
          toggleActiveDeliveryMan={toggleActiveDeliveryMan}
          printDeliveredBarcodeHandler={printDeliveredBarcodeHandler}
        />
      )}
    </div>
  )
}
